import { MessageCircle } from 'lucide-react';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { GuestProfileImage, GuestSpendStats } from '@Components/Guest';
import ShowFor from '@Components/ShowFor';
import { formatPhone } from '../../utils';
import { useFishDetails } from './FishDetailsContext';
import FishActionsMenu from './FishActionsMenu';

const currency = (value) =>
  value
    ? parseFloat(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : '$0.00';

const titleCase = (value) =>
  value
    ?.split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export default function FishDetailBanner({ variant }) {
  const {
    actor,
    activity,
    displaySubscriptionStatus,
    fishTags,
    handleCloudinaryUpload,
    id,
    setModalContent,
    openModal,
  } = useFishDetails();

  const profile = actor?.memberProfile ?? actor?.fishProfiles?.[0];
  const fullName = titleCase(`${profile?.fName ?? ''} ${profile?.lName ?? ''}`.trim());
  const email = actor?.memberProfile?.email ?? profile?.email;
  const isMember = !!actor?.subscriptions?.length;

  const roleLabel =
    variant === 'team-member' ? 'Team Member' : isMember ? 'Member' : 'Customer';

  return (
    <div className="dark flex flex-col gap-4 bg-zinc-800 px-4 py-5 sm:px-8">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-5">
          <div className="flex flex-col items-center gap-2">
            <GuestProfileImage
              cloudinaryPublicId={actor?.mostRecentAsset?.cloudinaryId}
              imageUrl={null}
              alt={fullName || 'Profile'}
              className="size-20 sm:size-28"
            />
            {/* Profile photos come from the member, not staff, so admins have
                no reason to set one. Kept in case that changes.
            <ShowFor permissions={['MEMBERS_WRITE']}>
              <Input
                id="profile-image-upload"
                type="file"
                accept="image/*"
                onChange={handleCloudinaryUpload}
                className="hidden"
                data-testid={'ui-profile-image-upload'}
              />
              <button
                type="button"
                data-testid={'ui-profile-image-upload-button'}
                onClick={() =>
                  document.getElementById('profile-image-upload')?.click()
                }
                className="cursor-pointer text-[10px] text-zinc-400 underline hover:text-zinc-200"
              >
                Edit photo
              </button>
            </ShowFor>
            */}
          </div>

          <div className="flex flex-col gap-2">
            <div>
              <h1 className="text-xl font-semibold text-white sm:text-2xl">
                {fullName || 'Unknown'}
              </h1>
              <p className="text-sm text-zinc-400">{roleLabel}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {displaySubscriptionStatus?.status && (
                <Badge
                  className="text-black"
                  style={{
                    backgroundColor: displaySubscriptionStatus.color,
                  }}
                >
                  {displaySubscriptionStatus.status}
                </Badge>
              )}
              {fishTags?.[id]?.map((tag) => (
                <Badge className="bg-[#CAAB6A] text-black" key={tag.id}>
                  {tag.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {actor?.phoneNumber && (
            <div className="flex h-12 items-center gap-2 rounded-xl bg-zinc-900 px-3 py-2">
              <span className="text-sm leading-4 font-medium text-white">
                {formatPhone(actor.phoneNumber)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setModalContent('sms');
                  openModal();
                }}
                className="border-input gap-2 rounded-md bg-white/5 shadow-xs"
              >
                <MessageCircle className="size-4" />
                SMS
              </Button>
            </div>
          )}
          {email && (
            <div className="flex h-12 items-center gap-2 rounded-xl bg-zinc-900 px-3 py-2">
              <span className="text-sm leading-4 font-medium break-all text-white">
                {email}
              </span>
            </div>
          )}
          <FishActionsMenu variant={variant} />
        </div>
      </div>

      {variant !== 'team-member' && (
        <GuestSpendStats
          className="justify-start gap-6 sm:gap-8"
          spendSummary={{
            mtdSpend: currency(activity?.meta?.mtdTotal),
            ytdSpend: currency(activity?.meta?.ytdTotal),
            allTimeSpend: currency(activity?.meta?.allTotal),
          }}
        />
      )}
    </div>
  );
}
